/*
 *  -----------------------------------------------------------------------
 *    <copyright file="generate-report-dialog.helper.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import {
    GeneratedReport,
    ReportModules,
    ReportSignatory,
    RiskReview,
    Signatory,
} from '@interfaces/report-templates.interface';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import { IReportFormInput } from '@components/shared/generate-report-dialog/generate-report-dialog.interface';
import { SignatoryGridItem } from '@components/shared/signatories-editable-grid/signatories-editable-grid.interface';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { dataSets_OpraQueries_dataSets_DataSet as dataSets_dataSets } from '@queries/generated/central-monitoring.generated';

interface IWorker {
    firstName?: string | null;
    lastName?: string | null;
    jobTitle?: string | null;
}

export const getFullNameAndJob = <T extends IWorker>(item: T) =>
    `${item.firstName} ${item.lastName}${item.jobTitle ? ` - ${item.jobTitle}` : ''}`;

export const formSignatoriesList = (data: Signatory[]) =>
    data.map((signatory) => ({
        id: signatory.id,
        text: getFullNameAndJob(signatory),
    }));

export const sortDataSets = (dataSetsArr: (dataSets_dataSets | null)[]): (dataSets_dataSets | null)[] => {
    return dataSetsArr.sort((a, b) => {
        // Returns datasets ordered from newest to oldest
        return new Date(b?.dataCutDate).valueOf() - new Date(a?.dataCutDate).valueOf();
    });
};

export const getDataSetsList = (dataSetsArr: (dataSets_dataSets | null)[]): SelectWithSearchDataArrayInterface[] => {
    const dataSetsForSelect = dataSetsArr.map((dataSet) => ({
        id: dataSet?.id,
        text: stringDateWithoutTimeFromUtc(dataSet?.dataCutDate, 'DD-MMM-YYYY'),
    }));
    dataSetsForSelect.unshift({ id: 'default', text: '' });

    return dataSetsForSelect;
};

export const formRiskReviewList = (riskReviews: RiskReview[]) =>
    riskReviews.map((riskReview) => ({
        id: riskReview.id,
        text: riskReview.description,
    }));

export const formGeneratedReportSubmitData = (
    data: Partial<IReportFormInput>,
    signatories: SignatoryGridItem[],
    reportTemplateId: string,
    orgKey: string,
    studyId: string,
    module: ReportModules,
    studyName: string,
) => {
    const dataToSubmit: Partial<GeneratedReport> = {
        ...data,
        comparisonDatasetId: data.comparisonDatasetId !== 'default' ? data.comparisonDatasetId : '',
        orgKey,
        studyId,
        reportTemplateId,
        module,
        studyName,
        signatories: signatories.map(
            (signatory): ReportSignatory => ({
                signatoryId: signatory.signatory ?? undefined,
                displayOrder: signatory.order,
            }),
        ),
    };

    if (module === Constants.Modules.CM) {
        delete dataToSubmit.riskReviewId;
    } else {
        delete dataToSubmit.datasetId;
        delete dataToSubmit.comparisonDatasetId;
    }

    return dataToSubmit;
};

export const formSignatoryGridItems = (signatories: ReportSignatory[]) =>
    signatories.map((signatory, index) => ({
        signatory: signatory.signatoryId ?? null,
        order: signatory.displayOrder ?? index,
    })) as SignatoryGridItem[];
